import { Component, inject, OnInit } from '@angular/core';
import { ModalBodyClass } from '../../../../modals/modal-body';
import { DetailedModalComponent } from '../../../../modals/detailed-modal/detailed-modal.component';
import { ZiroFormConfig } from '../../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import {
  AssociatedVoiceApplication,
  ResourceAccount,
  ResourceAccountType,
} from '../../../../shared/models/generated/ziroModels';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ResourceAccountsResource } from '../../../../resource-accounts/resource-accounts.resource';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ToastService } from '../../../../shared/services/toast.service';

export interface EditResourceAccountModalProperties {
  upn: string;
  displayName: string;
  resourceAccountType: ResourceAccountType;
  associatedVoiceApplication: AssociatedVoiceApplication | null;
  tenantId: string;
}

interface EditResourceAccountFormData {
  upn: string;
  displayName: string;
  voiceApplication: ZiroSelectOption;
}

@Component({
  selector: 'ziro-edit-resource-account-modal',
  templateUrl: './edit-resource-account-modal.component.html',
  standalone: true,
  imports: [ZiroFormsModule],
})
export class EditResourceAccountModalComponent extends ModalBodyClass<EditResourceAccountFormData> implements OnInit {
  private _modalComponent = inject<DetailedModalComponent<EditResourceAccountModalComponent>>(DetailedModalComponent);
  private _resourceAccountsResource = inject(ResourceAccountsResource);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);

  formConfig: ZiroFormConfig;

  private _upn: string;
  private _tenantId: string;
  private _originalResourceAccountType: ResourceAccountType;

  ngOnInit(): void {
    const props: EditResourceAccountModalProperties = this._modalComponent.modalViewProperties;
    this._upn = props.upn;
    this._tenantId = props.tenantId;
    this._originalResourceAccountType = props.resourceAccountType;

    const isKnownType =
      props.resourceAccountType === ResourceAccountType.AUTO_ATTENDANT ||
      props.resourceAccountType === ResourceAccountType.CALL_QUEUE;

    const voiceApplicationOptions: ZiroSelectOption[] = isKnownType
      ? [
          {
            label: this._translateService.instant(
              'tkey;resource_accounts.create_form.voice_application.type.auto_attendant',
            ),
            value: ResourceAccountType.AUTO_ATTENDANT,
          },
          {
            label: this._translateService.instant(
              'tkey;resource_accounts.create_form.voice_application.type.call_queue',
            ),
            value: ResourceAccountType.CALL_QUEUE,
          },
        ]
      : [];

    this.formConfig = {
      fields: {
        upn: {
          label: 'tkey;resource_accounts.create_form.upn.label',
          dataAutomation: 'resource-account-edit-upn-input',
          disabled: () => true,
          disabledTooltip: 'tkey;microsoft_360.resource_accounts.edit_modal.upn.tooltip',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
          }),
        },
        displayName: {
          label: 'tkey;resource_accounts.create_form.name.label',
          dataAutomation: 'resource-account-edit-name-input',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
            placeholder: 'tkey;resource_accounts.create_form.name.placeholder',
          }),
          required: true,
        },
        voiceApplication: {
          label: 'tkey;resource_accounts.create_form.voice_application.label',
          dataAutomation: 'resource-account-edit-voice-application',
          disabled: props.associatedVoiceApplication ? () => true : undefined,
          disabledTooltip: props.associatedVoiceApplication
            ? 'tkey;microsoft_360.resource_accounts.edit_modal.voice_application.disabled.tooltip'
            : undefined,
          componentConfig: new ZiroSelectConfig({
            options: voiceApplicationOptions,
          }),
        },
      },
    };

    this.entitySource.next({
      upn: props.upn,
      displayName: props.displayName,
      voiceApplication: voiceApplicationOptions.find((opt) => opt.value === props.resourceAccountType) ?? null,
    });
  }

  submit(): Observable<ResourceAccount> {
    this._validateAndSubmitSource.next(false);

    const updatedResourceAccount: ResourceAccount = {
      userPrincipalName: this._upn,
      displayName: this.entity.displayName,
      resourceAccountType:
        (this.entity.voiceApplication?.value as ResourceAccountType) ?? this._originalResourceAccountType,
    };

    return this._resourceAccountsResource.updateResourceAccount(this._tenantId, this._upn, updatedResourceAccount).pipe(
      map(() => {
        this._toastService.pushSaveToast(
          'tkey;resource_accounts.list.toast.service_type',
          updatedResourceAccount.displayName,
          ZiroIcons.SAVE_SUCCESS,
        );
        return updatedResourceAccount;
      }),
    );
  }
}
